import { useState, useEffect } from "react";
import axios from "axios";
import { ListenerLayout } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Users, FileText, Plus, Pencil, Trash2, Sparkles, 
  Calendar, MessageSquare, ChevronRight, Loader2, Search,
  User, Clock, BookOpen
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserNotes() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userNotes, setUserNotes] = useState([]);
  const [userSummary, setUserSummary] = useState(null);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Note dialog state
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteContent, setNoteContent] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/listener/my-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  };

  const selectUser = async (user) => {
    setSelectedUser(user);
    setLoadingNotes(true);
    setLoadingSummary(true);
    
    const token = localStorage.getItem("token");
    
    try {
      // Fetch notes and summary in parallel
      const [notesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/listener/notes/${user.user_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/listener/user-summary/${user.user_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setUserNotes(notesRes.data);
      setUserSummary(summaryRes.data);
    } catch (error) {
      console.error("Error fetching user details:", error);
      toast.error("Error al cargar detalles del usuario");
    } finally {
      setLoadingNotes(false);
      setLoadingSummary(false);
    }
  };

  const openNewNoteDialog = () => {
    setEditingNote(null);
    setNoteContent("");
    setShowNoteDialog(true);
  };

  const openEditNoteDialog = (note) => {
    setEditingNote(note);
    setNoteContent(note.content);
    setShowNoteDialog(true);
  };

  const saveNote = async () => {
    if (!noteContent.trim()) {
      toast.error("La nota no puede estar vacía");
      return;
    }

    setSaving(true);
    const token = localStorage.getItem("token");

    try {
      if (editingNote) {
        // Update existing note
        await axios.put(`${API}/listener/notes/${editingNote.id}`, 
          { content: noteContent },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Nota actualizada");
      } else {
        // Create new note
        await axios.post(`${API}/listener/notes`, 
          { user_id: selectedUser.user_id, content: noteContent },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Nota guardada");
      }

      // Refresh notes and summary
      setShowNoteDialog(false);
      await selectUser(selectedUser);
      
    } catch (error) {
      console.error("Error saving note:", error);
      toast.error("Error al guardar nota");
    } finally {
      setSaving(false);
    }
  };

  const deleteNote = async (noteId) => {
    if (!confirm("¿Estás seguro de eliminar esta nota?")) return;

    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API}/listener/notes/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Nota eliminada");
      await selectUser(selectedUser);
    } catch (error) {
      console.error("Error deleting note:", error);
      toast.error("Error al eliminar nota");
    }
  };

  const filteredUsers = users.filter(user =>
    user.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.user_email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <ListenerLayout title="Mis Usuarios">
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </ListenerLayout>
    );
  }

  return (
    <ListenerLayout title="Mis Usuarios">
      <div className="space-y-6">

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Users List */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Usuarios ({filteredUsers.length})
                </CardTitle>
                <div className="relative mt-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <input
                    type="text"
                    placeholder="Buscar usuario..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                    data-testid="search-users"
                  />
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {filteredUsers.length === 0 ? (
                  <div className="p-6 text-center text-stone-500">
                    <User className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                    <p>No tienes usuarios aún</p>
                    <p className="text-sm">Aparecerán aquí cuando tengas sesiones</p>
                  </div>
                ) : (
                  <div className="max-h-[500px] overflow-y-auto">
                    {filteredUsers.map((user) => (
                      <button
                        key={user.user_id}
                        onClick={() => selectUser(user)}
                        className={`w-full p-4 text-left border-b border-stone-100 last:border-0 hover:bg-stone-50 transition-colors ${
                          selectedUser?.user_id === user.user_id ? 'bg-primary/5 border-l-4 border-l-primary' : ''
                        }`}
                        data-testid={`user-${user.user_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-foreground truncate">{user.user_name}</p>
                            <p className="text-sm text-stone-500 truncate">{user.user_email}</p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-stone-400">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {user.session_count} sesiones
                              </span>
                              {user.my_notes_count > 0 && (
                                <span className="flex items-center gap-1">
                                  <FileText className="w-3 h-3" />
                                  {user.my_notes_count} notas
                                </span>
                              )}
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-stone-400 flex-shrink-0" />
                        </div>
                        {user.summary_topics && user.summary_topics.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {user.summary_topics.slice(0, 2).map((topic, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">
                                {topic}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* User Details */}
          <div className="lg:col-span-2 space-y-6">
            {!selectedUser ? (
              <Card className="p-12 text-center">
                <BookOpen className="w-16 h-16 mx-auto mb-4 text-stone-300" />
                <h3 className="text-xl font-medium text-stone-600 mb-2">Selecciona un usuario</h3>
                <p className="text-stone-500">Elige un usuario de la lista para ver su historial y notas</p>
              </Card>
            ) : (
              <>
                {/* AI Summary Card */}
                <Card className="bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-violet-800">
                      <Sparkles className="w-5 h-5" />
                      Resumen IA
                    </CardTitle>
                    <CardDescription className="text-violet-600">
                      Resumen generado automáticamente (visible para todos los listeners)
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {loadingSummary ? (
                      <div className="flex items-center gap-2 text-violet-600">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Cargando resumen...
                      </div>
                    ) : userSummary ? (
                      <div className="space-y-4">
                        <p className="text-stone-700 leading-relaxed">{userSummary.summary}</p>
                        
                        {userSummary.topics && userSummary.topics.length > 0 && (
                          <div>
                            <p className="text-sm text-stone-500 mb-2">Temas trabajados:</p>
                            <div className="flex flex-wrap gap-2">
                              {userSummary.topics.map((topic, i) => (
                                <Badge key={i} className="bg-violet-100 text-violet-700 hover:bg-violet-200">
                                  {topic}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="flex items-center gap-4 text-sm text-stone-500 pt-2 border-t border-violet-200">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {userSummary.total_sessions} sesiones
                          </span>
                          {userSummary.last_session_date && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              Última: {userSummary.last_session_date}
                            </span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <p className="text-stone-500">No hay resumen disponible aún</p>
                    )}
                  </CardContent>
                </Card>

                {/* Private Notes Card */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Mis Notas Privadas
                      </CardTitle>
                      <CardDescription>
                        Solo tú puedes ver estas notas
                      </CardDescription>
                    </div>
                    <Button onClick={openNewNoteDialog} data-testid="add-note-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Nueva Nota
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {loadingNotes ? (
                      <div className="flex items-center gap-2 text-stone-500">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Cargando notas...
                      </div>
                    ) : userNotes.length === 0 ? (
                      <div className="text-center py-8">
                        <MessageSquare className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                        <p className="text-stone-500 mb-4">No tienes notas sobre este usuario</p>
                        <Button variant="outline" onClick={openNewNoteDialog}>
                          <Plus className="w-4 h-4 mr-2" />
                          Crear primera nota
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {userNotes.map((note) => (
                          <div
                            key={note.id}
                            className="p-4 bg-stone-50 rounded-lg border border-stone-100"
                            data-testid={`note-${note.id}`}
                          >
                            <p className="text-stone-700 whitespace-pre-wrap leading-relaxed">
                              {note.content}
                            </p>
                            <div className="flex items-center justify-between mt-3 pt-3 border-t border-stone-200">
                              <span className="text-xs text-stone-400">
                                {new Date(note.created_at).toLocaleDateString('es-MX', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                                {note.updated_at !== note.created_at && ' (editado)'}
                              </span>
                              <div className="flex gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditNoteDialog(note)}
                                  className="h-8 px-2"
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => deleteNote(note.id)}
                                  className="h-8 px-2 text-red-500 hover:text-red-600 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </div>

        {/* Note Dialog */}
        <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingNote ? 'Editar Nota' : 'Nueva Nota'}
              </DialogTitle>
              <DialogDescription>
                {selectedUser && (
                  <>Nota sobre <strong>{selectedUser.user_name}</strong>. Solo tú podrás verla.</>
                )}
              </DialogDescription>
            </DialogHeader>
            
            <Textarea
              placeholder="Escribe tus notas aquí... (ej: Tema principal de la sesión, progreso observado, técnicas sugeridas)"
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              rows={6}
              className="resize-none"
              data-testid="note-content-input"
            />

            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-800">
                <strong>Nota:</strong> El sistema generará un resumen anónimo de tus notas 
                para ayudar a otros listeners. No incluyas información sensible como nombres 
                completos, direcciones o diagnósticos.
              </p>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNoteDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={saveNote} disabled={saving} data-testid="save-note-btn">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  'Guardar Nota'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </ListenerLayout>
  );
}
